import React, { useState, useEffect } from 'react';
import { FaSearch, FaPlus, FaTimes } from 'react-icons/fa';
import './css_pages/ProductCategories.css';
import {
  getAllCategories,
  createCategory,
  updateCategory,
  deleteCategory
} from '../services/categoriesServices';
import { getAllProductTypes } from '../services/productTypesService';

function ProductCategories() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProductTypeModal, setShowProductTypeModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [advancedSearchProductType, setAdvancedSearchProductType] = useState('');
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [productTypeSearch, setProductTypeSearch] = useState('');

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchCategories();
    fetchProductTypes();
  }, []);

  // Obtener todas las categorías
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      setError('Error loading categories');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Obtener todos los tipos de productos
  const fetchProductTypes = async () => {
    try {
      const response = await getAllProductTypes();
      if (response.success) {
        setProductTypes(response.data);
      }
    } catch (error) {
      console.error('Error loading product types:', error);
    }
  };

  // Filtrar categorías según búsqueda
  const filteredCategories = categories.filter(category => {
    const matchesName = category.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProductType = advancedSearchProductType === '' || 
      category.productTypeName.toLowerCase().includes(advancedSearchProductType.toLowerCase());
    return matchesName && matchesProductType;
  });

  // Agrupar categorías por tipo de producto
  const groupedCategories = filteredCategories.reduce((acc, category) => {
    const key = category.productTypeId;
    if (!acc[key]) {
      acc[key] = {
        productTypeId: category.productTypeId,
        productTypeName: category.productTypeName,
        categories: []
      };
    }
    acc[key].categories.push(category);
    return acc;
  }, {});

  // Abrir modal para crear
  const handleOpenModal = () => {
    setShowModal(true);
    setNewCategoryName('');
    setSelectedProductType(null);
    setEditingId(null);
  };

  // Abrir modal de selección de Product Type
  const handleOpenProductTypeModal = () => {
    setShowProductTypeModal(true);
    setProductTypeSearch('');
  };

  // Seleccionar Product Type
  const handleSelectProductType = (productType) => {
    setSelectedProductType(productType);
    setShowProductTypeModal(false);
  };

  // Abrir modal para editar
  const handleEditModal = (category) => {
    setShowModal(true);
    setNewCategoryName(category.name);
    setSelectedProductType({
      id: category.productTypeId,
      name: category.productTypeName
    });
    setEditingId(category.id);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setNewCategoryName('');
    setSelectedProductType(null);
    setEditingId(null);
  };

  // Guardar o actualizar categoría
  const handleSaveCategory = async () => {
    const normalizedName = newCategoryName.trim();
    
    if (!selectedProductType) {
      alert('Please select a product type');
      return;
    }
    
    if (normalizedName === '') {
      alert('Please enter a name');
      return;
    }

    try {
      setLoading(true);
      
      if (editingId) {
        const response = await updateCategory(editingId, {
          productTypeId: selectedProductType.id,
          name: normalizedName
        });
        if (response.success) {
          alert('Category updated successfully');
          await fetchCategories();
          handleCloseModal();
        }
      } else {
        const response = await createCategory({
          productTypeId: selectedProductType.id,
          name: normalizedName
        });
        if (response.success) {
          alert('Category created successfully');
          await fetchCategories();
          handleCloseModal();
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        alert('There is already a category with that name for this type of product');
      } else {
        alert('Error saving category');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal de confirmación de eliminación
  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Cerrar modal de eliminación
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const response = await deleteCategory(deleteId);
      if (response.success) {
        alert('Category successfully removed');
        await fetchCategories();
      }
      handleCloseDeleteModal();
    } catch (error) {
      alert('Error deleting the category');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar product types en el modal
  const filteredProductTypes = productTypes.filter(pt =>
    pt.name.toLowerCase().includes(productTypeSearch.toLowerCase())
  );

  return (
    <div className="product-categories-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1>Product categories</h1>
        </div>
        <button className="btn-primary" onClick={handleOpenModal} disabled={loading}>
          <FaPlus /> Create new product category
        </button>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchCategories}>Retry</button>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table - Desktop */}
      <div className="table-container desktop-only">
        {loading && <div className="loading">Loading...</div>}
        
        <table className="data-table">
          <thead>
            <tr>
              <th>Product Type</th>
              <th>Category</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(groupedCategories).length === 0 ? (
              <tr>
                <td colSpan="3" style={{ textAlign: 'center', padding: '40px' }}>
                  {loading ? 'Loading...' : 'No results found'}
                </td>
              </tr>
            ) : (
              Object.values(groupedCategories).map((group) => (
                <React.Fragment key={group.productTypeId}>
                  {group.categories.map((category, index) => (
                    <tr key={category.id}>
                      {index === 0 && (
                        <td rowSpan={group.categories.length} className="product-type-cell">
                          {group.productTypeName}
                        </td>
                      )}
                      <td>{category.name}</td>
                      <td>
                        <button 
                          className="btn-action" 
                          onClick={() => handleEditModal(category)}
                          disabled={loading}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn-action btn-delete" 
                          onClick={() => handleOpenDeleteModal(category.id)}
                          disabled={loading}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Cards - Mobile */}
      <div className="categories-cards-container mobile-only">
        {loading && <div className="loading">Loading...</div>}
        
        {Object.keys(groupedCategories).length === 0 ? (
          <div className="no-results">
            {loading ? 'Loading...' : 'No results found'}
          </div>
        ) : (
          Object.values(groupedCategories).map((group) => (
            <div key={group.productTypeId} className="product-type-group">
              <div className="product-type-group-header">
                <h3>{group.productTypeName}</h3>
                <span className="category-count">{group.categories.length} categor{group.categories.length !== 1 ? 'ies' : 'y'}</span>
              </div>
              
              {group.categories.map((category) => (
                <div key={category.id} className="category-card">
                  <div className="category-card-header">
                    <h4 className="category-card-name">{category.name}</h4>
                  </div>

                  <div className="category-card-footer">
                    <button 
                      className="btn-card-action btn-edit" 
                      onClick={() => handleEditModal(category)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-card-action btn-delete-card" 
                      onClick={() => handleOpenDeleteModal(category.id)}
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Modal Crear/Editar */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit product category' : 'Create new product category'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Product Type *</label>
                <div className="pick-container">
                  <button 
                    className="btn-pick" 
                    onClick={handleOpenProductTypeModal}
                    disabled={loading}
                  >
                    <FaSearch /> Pick
                  </button>
                  {selectedProductType && (
                    <div className="selected-item">
                      {selectedProductType.name}
                      <button 
                        className="btn-remove-selection"
                        onClick={() => setSelectedProductType(null)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter the category name"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseModal} disabled={loading}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveCategory} disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Seleccionar Product Type */}
      {showProductTypeModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-picker" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select an item</h2>
              <button className="btn-close" onClick={() => setShowProductTypeModal(false)}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Search..."
                  value={productTypeSearch}
                  onChange={(e) => setProductTypeSearch(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="picker-list">
                <table className="picker-table">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProductTypes.map((pt) => (
                      <tr key={pt.id}>
                        <td>
                          <button 
                            className="btn-select-item"
                            onClick={() => handleSelectProductType(pt)}
                          >
                            ✓
                          </button>
                        </td>
                        <td>{pt.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminación */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm deletion</h2>
              <button className="btn-close" onClick={handleCloseDeleteModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to delete this category?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseDeleteModal} disabled={loading}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleConfirmDelete} disabled={loading}>
                {loading ? 'Removing...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductCategories;